import React, { useEffect, useState } from 'react'
import moment from 'moment'
import {
    FbButton,
    FbCollapse, FbFormikCheckbox,
    FbFormikSelect,
    GridContainer,
    GridItem,
    LoadingIndicator,
    Typography
} from '@fabricks/ui-components'
import styled from 'styled-components'
import {
    Field,
    FieldInputProps,
    FieldMetaProps,
    Form as FormFormik,
    Formik,
    FormikContextType,
    FormikProps,
    FormikValues,
    useField,
    useFormikContext,
} from 'formik'
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'
import { get as _get } from 'lodash'
import { useKeycloak } from 'react-keycloak'
import Swal from 'sweetalert2'
import axios from 'axios'
import { Skeleton } from '@material-ui/lab'
import {
    createBaseContext,
    getContext,
    useCustomerGroups, useGetTenants, useProcessLogs,
    useRackPerZone,
    useZones
} from '../../../services/apiService'
import {
    BoxTypeDto,
    FilterParam,
    GenericDto, GetProcessLogsQuery,
    PageableParam,
    PickZoneDto, ProcessLogDto, ProcessLogMetaParamDto,
    SortDirection,
    SortParam, TenantDto, useGetMeQuery, useGetProcessLogsLazyQuery, useOptimizeStorageLocationsForZonesMutation,
} from '../../../api/models'
import getEnvVars from '../../../environment/environment'
import { FormElementProps } from '../../../utils/formGenerator'
import { FbFormikSlider, getFilter, isFormElementVisible } from '../../../utils/formElements'
import { downloadFile, extractError, handleResponseWithSwal } from '../../../utils/apiUtils'
import { useBoxtypes, usePickZones } from '../../../services/dataService'
import { ProcessOverview } from "../../../components/ProcessOverview/processOverview";
import { showError, showSuccess } from "../../../utils/sweetAlertHelper";
import { hashCode } from "../../../utils/formHelper";
import { BoxTypeOptimizationLoading } from "../BoxTypeOptimization/BoxTypeOptimization";

interface StorageLocationOptimizationProps {
    initLoadingTime?: number
}

const ProgressContainer = styled.div`
    margin: 0px 0px 15px 0px;
`

export interface FormikField {
    name: string
    label: string
    childProps?: any
}

export const CustomerGroupFormField = (props: FormikField) => {
    const {
        name,
        childProps,
    } = props
    const form = useFormikContext()
    const [field, meta] = useField(name)
    const query = useCustomerGroups

    const onData = (data: any) => {
        return _get(data, 'getCustomerGroups.data', []).map((cg: GenericDto) => ({
            label: cg.value,
            value: cg.uuid,
        }))
    }

    return (
        <GenericFormSelect
            formik={{ field, meta, form }}
            onData={onData}
            query={query}
            label="Picking groups"
            queryConfig={{
                pagination: { page: 0, size: 35 },
                filter: [{ field: 'isActive', expected: 'true' }],
                sort: [{ field: 'name', order: SortDirection.Asc }],
            }}
            childProps={childProps}
        />
    )
}

export const ProcessLogFormField = (props: FormikField) => {
    const {
        name,
        childProps,
    } = props
    const form = useFormikContext()
    const [field, meta] = useField(name)
    const query = useProcessLogs

    const renderDate = (value: any) => {
        return value ? moment(Number.parseInt(value, 10)).format("DD.MM.YYYY HH:mm:ss") : ""
    }

    const onData = (data: any) => {
        return _get(data, 'getProcessLogs.data', []).map((cg: ProcessLogDto) => ({
            label: renderDate(cg.start),
            value: cg.uuid,
        }))
    }

    return (
        <GenericFormSelect
            formik={{ field, meta, form }}
            onData={onData}
            query={query}
            label="Result"
            queryConfig={{
                pagination: { page: 0, size: 10 },
                filter: [{ field: 'type', expected: 'OPTIMIZE_FUEL_LEVEL' }, {field: 'status', expected: 'FINISHED' }],
                sort: [{ field: 'start', order: 'DESC' as SortDirection }]
            }}
            childProps={childProps}
        />
    )
}

export const ZoneFormField = (props: FormikField) => {
    const {
        name,
        childProps,
    } = props
    const form = useFormikContext()
    const [field, meta] = useField(name)
    const query = useZones

    const onData = (data: any) => {
        return _get(data, 'getZones.data', []).map((cg: GenericDto) => ({
            label: cg.value,
            value: cg.uuid,
        }))
    }

    return (
        <GenericFormSelect
            formik={{ field, meta, form }}
            onData={onData}
            query={query}
            label="Zones"
            queryConfig={{
                pagination: { page: 0, size: 35 },
                filter: [{ field: 'isActive', expected: 'true' }, { field: 'effCalcDone', expected: 'true' }],
                sort: [{ field: 'name', order: SortDirection.Asc }],
            }}
            childProps={childProps}
        />
    )
}

export const PickZoneSelectFormField = (props: FormikField) => {
    const {
        name,
        label,
        childProps,
    } = props
    const form = useFormikContext()
    const [field, meta] = useField(name)
    const query = usePickZones

    const onData = (data: any) => {
        return _get(data, 'getPickZones.data', []).map((pickzone: PickZoneDto) => ({
            label: pickzone.name,
            value: pickzone.uuid,
        }))
    }

    return (
        <GenericFormSelect
            formik={{ field, meta, form }}
            onData={onData}
            query={query}
            queryConfig={{
                pagination: { page: 0, size: 35 },
                filter: [],
                sort: [{ field: 'name', order: SortDirection.Asc }],
            }}
            label={label}
            childProps={childProps}
        />
    )
}

export const PickZoneSelectFormElement = (props: FormElementProps) => {
    const {
        name,
        childProps,
        config: { visibility, filter },
    } = props
    const form = useFormikContext()
    const [field, meta] = useField(name)
    const isVisibleDeps = isFormElementVisible(field, form, props.config)
    const query = usePickZones

    if (!isVisibleDeps) {
        return null
    }

    const onData = (data: any) => {
        return _get(data, 'getPickZones.data', []).map((pickzone: PickZoneDto) => ({
            label: pickzone.name,
            value: pickzone.uuid,
        }))
    }

    return (
        <GenericFormSelect
            formik={{ field, meta, form }}
            onData={onData}
            query={query}
            queryConfig={{
                pagination: { page: 0, size: 35 },
                filter: [],
                sort: [{ field: 'name', order: SortDirection.Asc }],
            }}
            childProps={childProps}
        />
    )
}

export const CustomerGroupFormElement = (props: FormElementProps) => {
    const {
        name,
        childProps,
        config: { visibility, filter },
    } = props
    const form = useFormikContext()
    const [field, meta] = useField(name)
    const isVisibleDeps = isFormElementVisible(field, form, props.config)
    const query = useCustomerGroups

    if (!isVisibleDeps) {
        return null
    }

    const onData = (data: any) => {
        return _get(data, 'getCustomerGroups.data', []).map((cg: GenericDto) => ({
            label: cg.value,
            value: cg.uuid,
        }))
    }

    return (
        <GenericFormSelect
            formik={{ field, meta, form }}
            onData={onData}
            query={query}
            label="Picking groups"
            queryConfig={{
                pagination: { page: 0, size: 35 },
                filter: [{ field: 'isActive', expected: 'true' }],
                sort: [{ field: 'name', order: SortDirection.Asc }],
            }}
            childProps={childProps}
        />
    )
}

export const TenantGroupFormElement = (props: FormElementProps) => {
    const {
        name,
        childProps,
        config: { visibility, filter },
    } = props
    const form = useFormikContext()
    const [field, meta] = useField(name)
    const isVisibleDeps = isFormElementVisible(field, form, props.config)
    const query = useGetTenants

    if (!isVisibleDeps) {
        return null
    }

    const onData = (data: any) => {
        return _get(data, 'getTenants.data', []).map((cg: TenantDto) => ({
            label: cg.name,
            value: cg.uuid,
        }))
    }

    return (
        <GenericFormSelect
            formik={{ field, meta, form }}
            onData={onData}
            query={query}
            label="Tenant"
            queryConfig={{
                pagination: { page: 0, size: 35 },
                filter: [{ field: 'isActive', expected: 'true' }],
                sort: [{ field: 'name', order: SortDirection.Asc }],
            }}
            childProps={childProps}
        />
    )
}

export const BoxSelectFormElement = (props: FormElementProps) => {
    const {
        name,
        childProps,
        config: { visibility, filter },
    } = props
    const form = useFormikContext()
    const [field, meta] = useField(name)
    const isVisibleDeps = isFormElementVisible(field, form, props.config)
    const query = useBoxtypes

    if (!isVisibleDeps) {
        return null
    }

    const onData = (data: any) => {
        return _get(data, 'getBoxTypes.data', []).map((box: BoxTypeDto) => ({
            label: box.name,
            value: box.uuid,
        }))
    }

    return (
        <GenericFormSelect
            formik={{ field, meta, form }}
            onData={onData}
            query={query}
            label="Box Type"
            queryConfig={{
                pagination: { page: 0, size: 35 },
                filter: [{ field: 'isActive', expected: 'true' }],
                sort: [{ field: 'name', order: SortDirection.Asc }],
            }}
            childProps={childProps}
        />
    )
}

export const ZoneSelectFormElement = (props: FormElementProps) => {
    const {
        name,
        childProps,
        config: { visibility, filter },
    } = props
    const form = useFormikContext()
    const [field, meta] = useField(name)
    const isVisibleDeps = isFormElementVisible(field, form, props.config)
    const query = useZones

    if (!isVisibleDeps) {
        return null
    }

    const onData = (data: any) => {
        return _get(data, 'getZones.data', []).map((zone: GenericDto) => ({
            label: zone.value,
            value: zone.uuid,
        }))
    }

    return (
        <GenericFormSelect
            formik={{ field, meta, form }}
            onData={onData}
            query={query}
            label="Zone"
            childProps={childProps}
            queryConfig={{}}
        />
    )
}

export const RackSelectFormElement = (props: FormElementProps) => {
    const { name, config, childProps } = props
    const form = useFormikContext()
    const [field, meta] = useField(name)

    // Is visible
    const isVisible = isFormElementVisible(field, form, config)

    if (!isVisible) {
        return null
    }

    // Has Filter
    const derivedFilter = getFilter(form, config)

    const onData = (data: any) => {
        return _get(data, 'getRacksPerZone.data', []).map((zone: GenericDto) => ({
            label: zone.value,
            value: zone.uuid,
        }))
    }
    return (
        <GenericFormSelect
            label="Rack"
            formik={{ field, meta, form }}
            onData={onData}
            query={useRackPerZone}
            childProps={childProps}
            queryConfig={{ filter: derivedFilter }}
        />
    )
}

interface GenericFormSelectOption {
    label: string
    value: string
}

interface GenericFormSelectProps<T, S> {
    query: (token: string | undefined, params: any) => any
    onData: (data: any) => GenericFormSelectOption[]
    formik: {
        field: FieldInputProps<any>
        meta: FieldMetaProps<any>
        form: FormikContextType<unknown>
    }
    label?: string
    childProps: any
    queryConfig: {
        filter?: FilterParam[]
        pagination?: PageableParam
        sort?: SortParam[]
        q?: string
    }
}

export const GenericFormSelect = (props: GenericFormSelectProps<any, any>) => {
    const { query, onData, formik, label, childProps, queryConfig } = props
    const [keycloak] = useKeycloak()
    const [initLoading, setInitLoading] = useState(true)
    const [selectOptions, setSelectOptions] = useState<GenericFormSelectOption[]>([])

    const { data, loading, error } = query(keycloak.token, {
        filter: queryConfig.filter || [],
        pagination: queryConfig.pagination,
        sort: queryConfig.sort,
    })
    useEffect(() => {
        if (!loading && data) {
            const options = onData(data)
            setSelectOptions(options)
        }
        return () => {
        }
    }, [data, loading, onData, error])

    /* useEffect(() => {
        const isDefaultValue = (value: string) => value.length === 0
        const isValuePartOfOption = (value: string) => selectOptions.filter(option => option.value === value).length > 0
        if (!isValuePartOfOption(formik.field.value) && !isDefaultValue(formik.field.value)) {
            formik.form.setFieldValue(formik.field.name, '')
        }
    }, [formik.field.name, formik.field.value, formik.form, selectOptions]) */

    if (error) {
        console.error(error.message)
    }

    if (loading || initLoading) {
        setTimeout(() => setInitLoading(false), 1500)
        return <Skeleton variant="rect" animation="wave" width="100%" height={40} style={{ marginBottom: '15px' }} />
    }

    return <FbFormikSelect {...formik} options={selectOptions} label={label} {...childProps} />
}


export const StorageLocationOptimizationForm = (formProps: FormikProps<any>) => {
    const useStyles = makeStyles((theme: Theme) =>
        createStyles({
            root: {
                marginBottom: theme.spacing(2),
            },
        })
    )


    const classes = useStyles()

    const WithFillingCheckbox = (props: any) => {
        return <FbFormikCheckbox label="Focus on pick efficiency" {...props} />
    }

    return (
        <FormFormik>
            <ZoneFormField name="zones" label="Zone for optimizations" childProps={{ multiple: true }} />
            <FbFormikSlider name={"fillingLevel"} label={"Desired filling level (%)"} showValueLabel={"on"} min={0} max={100} marks={true} />
            <FbFormikSlider name={"maxBoxSkip"} label={"Number of box types for rearrangement"} showValueLabel={"on"} min={1} max={5} marks={true} />
            <FbFormikSlider name={"trendFactor"} label={"Trend factor (%)"} showValueLabel={"on"} min={0} max={100} marks={true} />
            <Field name="withFilling" placeholder="Focus on pick efficiency" component={WithFillingCheckbox} />
            <FbButton
                disabled={formProps.isSubmitting || false}
                className={classes.root}
                variant="contained"
                color="primary"
                type="submit"
            >
                Optimize
            </FbButton>
            <FbCollapse in={formProps.isSubmitting} collapsedHeight={0}>
                <ProgressContainer>
                    <LoadingIndicator color="secondary" variant="indeterminate" value={10} valueBuffer={3} />
                </ProgressContainer>
            </FbCollapse>
        </FormFormik>
    )
}


export const StorageLocationOptimization: React.FC<StorageLocationOptimizationProps> = (
    props: StorageLocationOptimizationProps
) => {
    const { initLoadingTime } = props
    const [keycloak] = useKeycloak()
    const [initLoading, setInitLoading] = useState((initLoadingTime || 0) > 0)


    const [executeMutation, resultOfMutation] = useOptimizeStorageLocationsForZonesMutation()
    const [initialValues, setInitialValues] = useState({ name: '', zones: [], fillingLevel: 80, withFilling: false, trendFactor: 25, maxBoxSkip: 3 })
    const { data: meData, loading: meLoading, error: meError, called: meCalled } = useGetMeQuery(
        createBaseContext(keycloak.token || '')
    )

    useEffect(() => {
        if (meError) {
            alert('Your desired filling level could not be determined')
        }

        if (meCalled && !meLoading) {
            const targetValue = _get(meData, 'getMe.data.0.tenant.config.desiredFuelLevel', null)
            if (targetValue) {
                setInitialValues(Object.assign(initialValues, { fillingLevel: targetValue * 100 }))
            }
        }
        return () => {}
    }, [meData, meError, meCalled, meLoading, initialValues])

    if (initLoading) {
        setTimeout(() => setInitLoading(false), initLoadingTime)
        return <BoxTypeOptimizationLoading />
    }

    const onSubmit = (values: FormikValues, actions: any) => {
        setTimeout(() => {
            executeMutation({
                context: getContext(keycloak.token),
                variables: { zones: values.zones, fillingLevel: values.fillingLevel / 100, withFilling: values.withFilling, trendFactor: values.trendFactor / 100, maxBoxSkip: values.maxBoxSkip },
            })
                .then(response => {
                    const error = extractError(response, 'optimizeStorageLocationsForZones')
                    if (error) {
                        showError(`${_get(error, 'message', 'unknown')} (${_get(error, 'code', 'XXX')})`, 'Error')
                    } else {
                        showSuccess('Successfully planned the optimization execution', 'Success')
                    }
                })
                .catch(e => {
                    console.error(e)
                })
                .finally(() => {
                    actions.setSubmitting(false)
                })
        }, 2500)
    }

    const onShowResult = (result: ProcessLogMetaParamDto[]) => {
        const score = JSON.parse(_get(result, '0.value', 0)) as number
        showSuccess(`You have reached an efficiency improvement of ${score.toFixed(2)} %`, 'Result')
    }



    return (
        <div style={{ marginBottom: '15px' }}>
            <Typography key={'note'} variant={'caption'} gutterBottom={true}>
                Be aware that only zones can be optimized with valid efficiency assignments. If one of your zones can not be selected, please check the zone configuration
            </Typography>
            <hr style={{ marginBottom: '30px'}}/>
            <GridContainer>
                <GridItem xs={12} sm={6} md={6}>
                    <Formik key={hashCode(JSON.stringify(initialValues))} initialValues={initialValues} onSubmit={onSubmit} render={StorageLocationOptimizationForm} enableReinitialize={true} />
                </GridItem>
                <GridItem xs={12} sm={6} md={6}>
                    <ProcessOverview onShowResult={onShowResult} type={'OPTIMIZE_STORAGE_LOCATION'} onRowClick={() => {} } />
                </GridItem>
            </GridContainer>
        </div>
    )
}
